/**
 * API Utility Functions
 * Centralized API communication with authentication support
 */

// API base URL configuration
// Development: http://localhost:3000
// Production: https://www.grysolle.com/api
export const API_BASE = (() => {
  // Check if we're in production (deployed on kxinvestor.com)
  const isProduction = window.location.hostname === 'www.kxinvestor.com' || 
                      window.location.hostname === 'kxinvestor.com';
  
  if (isProduction) {
    return 'https://www.kxinvestor.com';
  }
  
  // Development environment
  return 'http://localhost:3000';
})();

/**
 * Get authentication token from localStorage
 * @returns {string|null} Authentication token
 */
const getAuthToken = () => {
  return localStorage.getItem('token');
};

/**
 * Enhanced API fetch function with authentication and error handling
 * @param {string} path - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} API response
 */
export async function apiFetch(path, options = {}) {
  const token = getAuthToken();
  
  // Prepare headers with authentication
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  // Add authorization header if token exists and not already present
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers,
      ...options,
    });

    // Handle authentication errors
    if (res.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/wealth';
      throw new Error('Authentication required');
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`${res.status} ${res.statusText} ${text}`);
    }

    // Return null for 204 responses (no content)
    if (res.status === 204) return null;
    
    return res.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * GET request helper
 * @param {string} path - API endpoint path
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} API response
 */
export const apiGet = (path, options = {}) => {
  return apiFetch(path, { method: 'GET', ...options });
};

/**
 * POST request helper
 * @param {string} path - API endpoint path
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} API response
 */
export const apiPost = (path, data, options = {}) => {
  return apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  });
};

/**
 * PUT request helper
 * @param {string} path - API endpoint path
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} API response
 */
export const apiPut = (path, data, options = {}) => {
  return apiFetch(path, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options
  });
};

/**
 * PATCH request helper
 * @param {string} path - API endpoint path
 * @param {Object} data - Request body data
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} API response
 */
export const apiPatch = (path, data, options = {}) => {
  return apiFetch(path, {
    method: 'PATCH',
    body: JSON.stringify(data),
    ...options
  });
};

/**
 * DELETE request helper
 * @param {string} path - API endpoint path
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} API response
 */
export const apiDelete = (path, options = {}) => {
  return apiFetch(path, { method: 'DELETE', ...options });
};
